import { useEffect } from 'react';
import { useActivitySensor } from '@archipro-website/react-hooks';
import type { MeUser } from '~/server/types';
import { useNotifications } from '~/modules/notifications';
import { SPACING_PX, useChat } from '../hooks/use-chat';
import { useConversations } from '../hooks/use-conversations';
import { useConversationCache } from '../hooks/use-conversation-cache';
import { useRealTimeChat } from '../hooks/use-real-time-chat';
import { ChatInboxDrawer } from './chat-inbox/ChatInboxDrawer';
import { ChatBox } from './chat-box/ChatBox';

/**
 * The chat inbox drawer and chat boxes for the current user. This component
 * should only be rendered once in the root component.
 */
export function ChatBoxes({ user }: { user: MeUser }) {
    const { chatOpenState, chatConversationsList, setChatOpenState } =
        useChat();
    const { conversations, refetch } = useConversations();
    const { invalidateCachedConversation } = useConversationCache();
    const { notifications, refetch: refetchNotifications } = useNotifications();
    const { isConnected } = useRealTimeChat({
        onMessage: (message) => {
            refetch();
            refetchNotifications();
            // Invalidate cached conversation avoid showing outdated messages.
            invalidateCachedConversation(message.conversation_id);
        },
    });
    const pageActive = useActivitySensor();

    useEffect(() => {
        // Use polling to fetch the conversations list.
        let pollingInterval = 300_000; // 5 minutes
        if (!isConnected && pageActive) {
            // Fallback to polling if the WebSocket server is not connected.
            pollingInterval = 30_000; // 30 seconds
        }
        const interval = setInterval(() => {
            refetch();
        }, pollingInterval);
        return () => clearInterval(interval);
    }, [isConnected, pageActive, refetch]);

    useEffect(() => {
        // Show the chat messages button when there are new unread messages.
        if (chatOpenState === 'hidden' && notifications.userInbox?.count) {
            setChatOpenState('closed');
        }
        // Do not include chatOpenState in the dependencies to prevent
        // re-opening when the chat open state is changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications.userInbox?.count]);

    if (chatOpenState === 'hidden') {
        return null;
    }

    return (
        <ChatInboxDrawer
            className="fixed bottom-8 right-8 z-fixed"
            conversations={conversations}
            user={user}
        >
            <div
                className="flex min-w-0 flex-1 flex-row-reverse items-end justify-start"
                style={{
                    marginRight: `${SPACING_PX}px`,
                    gap: `${SPACING_PX}px`,
                }}
            >
                {chatConversationsList.map((conversationState) => (
                    <ChatBox
                        key={conversationState.id}
                        className="z-1 shrink-0"
                        user={user}
                        conversationID={conversationState.id}
                        conversation={conversations?.find(
                            (c) => c.ID === String(conversationState.id)
                        )}
                    />
                ))}
            </div>
        </ChatInboxDrawer>
    );
}
